// ============================================================
// TMR Portal Admin — leituras e escritas do molde global, sobre @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { globalRateKeys } from '../queryClient';
import { singlePageReader } from '../../lib/export/exportPageReader';
import type {
  CreateTmrRateRequest,
  TmrRate,
  TmrRateImpact,
  TmrRateList,
  TmrRateSyncResult,
  TmrRateWriteResult,
  UpdateTmrRateRequest,
} from './contract';

/**
 * The TMR's global rate template.
 *
 * A pure read: it never brings the template over from the legacy. When nothing is materialized here yet the
 * response says `needsImport` and the screen decides — importing is `POST /rates/global/sync`, gated by
 * `rates.global.manage` (`15` §10, D-A2).
 *
 * @param enabled Whether the operator holds `rates.global.read` — false skips a request that would 403.
 */
export function useTmrRates(enabled = true): UseQueryResult<TmrRateList> {
  return useQuery({
    queryKey: globalRateKeys.list(),
    queryFn: ({ signal }) => api.get<TmrRateList>('/rates/global', { signal }),
    enabled,
  });
}

/**
 * Reads the global template for **export** (20 §3).
 *
 * The list is not paged and should not be — the template is short by construction — so it arrives as a
 * single page and the walk ends after one request.
 */
export function globalRateExportPage() {
  return singlePageReader<TmrRate>(async () => (await api.get<TmrRateList>('/rates/global')).items);
}

/**
 * What propagating this line would reach, counted in the legacy right now.
 *
 * Never served from cache: it is the number the operator is deciding on, over rows that a coupon, the
 * ScriptCase back-office or a product creation can move between one reading and the next (`15` §8).
 *
 * @param rateId The template line, or undefined while no form is open.
 */
export function useTmrRateImpact(rateId: string | undefined): UseQueryResult<TmrRateImpact> {
  return useQuery({
    queryKey: globalRateKeys.impact(rateId ?? ''),
    queryFn: ({ signal }) => api.get<TmrRateImpact>(`/rates/global/${rateId}/impact`, { signal }),
    enabled: Boolean(rateId),
    staleTime: 0,
    gcTime: 0,
  });
}

/** Adds a line to the template. It reaches future Empresas only — creation never propagates. */
export function useCreateTmrRate() {
  return useTmrRateMutation((request: CreateTmrRateRequest) =>
    api.post<TmrRateWriteResult>('/rates/global', request));
}

/**
 * Changes a line of the template, and — only if the operator chose it — every Empresa that already holds it.
 */
export function useUpdateTmrRate() {
  return useTmrRateMutation(({ rateId, request }: { rateId: string; request: UpdateTmrRateRequest }) =>
    api.put<TmrRateWriteResult>(`/rates/global/${rateId}`, request));
}

/**
 * Removes a line from the template.
 *
 * Empresas that already copied it keep their own rate: this decides what a future Empresa starts with, not
 * what a current one is charged.
 */
export function useDeleteTmrRate() {
  return useTmrRateMutation((rateId: string) => api.delete<void>(`/rates/global/${rateId}`));
}

/**
 * Reconciles the template with `taxas_tmr`.
 *
 * It has a button of its own for the same reason the Empresa's rates do: the legacy is still a writer. The
 * ScriptCase back-office edits these tables directly, and until it is retired an import is the only way
 * back from an edit made over there (`15` §8).
 */
export function useSyncTmrRates() {
  return useTmrRateMutation(() => api.post<TmrRateSyncResult>('/rates/global/sync', {}));
}

/**
 * Shared plumbing for every template write: run it, then invalidate the template.
 *
 * Only the template — a propagation rewrites Empresa rates in the legacy through a worker, on its own
 * schedule, so there is no moment at which invalidating an Empresa's cache here would show the new value.
 * The Empresa's tab reads its own rates and reconciles them itself.
 *
 * @param mutationFn The request itself.
 */
function useTmrRateMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: globalRateKeys.all });
    },
  });
}
